import React, { useState } from "react";
import {
  useTranslate,
  useCustom,
  useCustomMutation,
} from "@refinedev/core";
import {
  Alert,
  Button,
  Card,
  Form,
  Input,
  Modal,
  Space,
  Spin,
  Tag,
  Typography,
  message,
} from "antd";
import {
  CopyOutlined,
  DisconnectOutlined,
  SaveOutlined,
} from "@ant-design/icons";

const { Text, Paragraph } = Typography;

interface IStripeConnectionStatus {
  connected: boolean;
  source: "tenant" | "env" | "none";
  connectedAt?: string;
  secretKeyLast4?: string;
  webhookUrl?: string;
}

interface StripeIntegrationCardProps {
  paymentEngine?: string;
  invoicingEngine?: string;
}

export const StripeIntegrationCard: React.FC<StripeIntegrationCardProps> = ({
  paymentEngine,
  invoicingEngine: _invoicingEngine,
}) => {
  const translate = useTranslate();
  const [form] = Form.useForm();
  const [isEditing, setIsEditing] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const {
    data: statusResponse,
    isLoading,
    isError,
    refetch,
  } = useCustom<IStripeConnectionStatus>({
    url: "integrations/stripe",
    method: "get",
  });

  const { mutate: saveConnection, isLoading: isSaving } = useCustomMutation();
  const { mutate: disconnect, isLoading: isDisconnecting } =
    useCustomMutation();

  const status = statusResponse?.data;
  const isTenantConnected = status?.source === "tenant";
  const isEnvFallback = status?.source === "env";
  const isInUse = paymentEngine === "stripe";
  const showForm = !isTenantConnected || isEditing;

  const closeModal = () => {
    setIsModalOpen(false);
    setIsEditing(false);
    form.resetFields();
  };

  const handleCopyWebhookUrl = async () => {
    if (!status?.webhookUrl) {
      return;
    }
    try {
      await navigator.clipboard.writeText(status.webhookUrl);
      message.success(
        translate(
          "invoice-settings.integrations.stripe.webhookCopied",
          "URL du webhook copiée",
        ),
      );
    } catch {
      message.error(
        translate(
          "invoice-settings.integrations.stripe.webhookCopyError",
          "Impossible de copier l'URL",
        ),
      );
    }
  };

  const handleSave = async (values: {
    secretKey: string;
    webhookSecret: string;
  }) => {
    saveConnection(
      {
        url: "integrations/stripe",
        method: "put",
        values: {
          secretKey: values.secretKey.trim(),
          webhookSecret: values.webhookSecret.trim(),
        },
      },
      {
        onSuccess: () => {
          message.success(
            translate(
              "invoice-settings.integrations.stripe.saveSuccess",
              "Configuration Stripe enregistrée",
            ),
          );
          form.resetFields();
          setIsEditing(false);
          setIsModalOpen(false);
          refetch();
        },
        onError: () => {
          message.error(
            translate(
              "invoice-settings.integrations.stripe.saveError",
              "Impossible d'enregistrer la configuration Stripe",
            ),
          );
        },
      },
    );
  };

  const handleDisconnect = () => {
    disconnect(
      {
        url: "integrations/stripe",
        method: "delete",
        values: {},
      },
      {
        onSuccess: () => {
          message.success(
            translate(
              "invoice-settings.integrations.stripe.disconnectedSuccess",
              "Configuration Stripe du tenant supprimée",
            ),
          );
          setIsEditing(false);
          setIsModalOpen(false);
          refetch();
        },
        onError: () => {
          message.error(
            translate(
              "invoice-settings.integrations.stripe.disconnectError",
              "Impossible de supprimer la configuration Stripe",
            ),
          );
        },
      },
    );
  };

  if (isLoading) {
    return (
      <Card className="mb-4">
        <Spin />
      </Card>
    );
  }

  if (isError) {
    return (
      <Card className="mb-4">
        <Alert
          type="warning"
          showIcon
          message={translate(
            "invoice-settings.integrations.stripe.unavailable",
            "Intégration Stripe indisponible",
          )}
          description={translate(
            "invoice-settings.integrations.stripe.unavailableHint",
            "L'intégration n'est pas activée ou n'est pas configurée sur le serveur.",
          )}
        />
      </Card>
    );
  }

  return (
    <>
      <Card className="mb-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <Space wrap>
            <Text strong>
              {translate(
                "invoice-settings.integrations.stripe.title",
                "Stripe",
              )}
            </Text>
            {isTenantConnected ? (
              <Tag color="success">
                {translate(
                  "invoice-settings.integrations.stripe.connected",
                  "Connecté",
                )}
              </Tag>
            ) : isEnvFallback ? (
              <Tag color="processing">
                {translate(
                  "invoice-settings.integrations.stripe.envFallback",
                  "Fallback plateforme",
                )}
              </Tag>
            ) : (
              <Tag>
                {translate(
                  "invoice-settings.integrations.collapsed.notConnected",
                  "Non connecté",
                )}
              </Tag>
            )}
            {isInUse && (
              <Tag color="blue">
                {translate(
                  "invoice-settings.engines.badgePayment",
                  "Paiement",
                )}
              </Tag>
            )}
          </Space>
          <Button type="primary" onClick={() => setIsModalOpen(true)}>
            {isTenantConnected || isEnvFallback
              ? translate(
                  "invoice-settings.integrations.collapsed.manage",
                  "Gérer",
                )
              : translate(
                  "invoice-settings.integrations.collapsed.configure",
                  "Configurer",
                )}
          </Button>
        </div>
      </Card>

      <Modal
        title={translate(
          "invoice-settings.integrations.stripe.modalTitle",
          "Configuration Stripe",
        )}
        open={isModalOpen}
        onCancel={closeModal}
        footer={null}
        destroyOnClose
        width={640}
      >
        <Paragraph type="secondary" className="!mb-4">
          {translate(
            "invoice-settings.integrations.stripe.description",
            "Configurez les clés Stripe de ce tenant pour encaisser les paiements par carte.",
          )}
        </Paragraph>

        {isEnvFallback && !isEditing && (
          <Alert
            className="mb-4"
            type="info"
            showIcon
            message={translate(
              "invoice-settings.integrations.stripe.envFallbackTitle",
              "Stripe plateforme actif",
            )}
            description={translate(
              "invoice-settings.integrations.stripe.envFallbackHint",
              "Ce tenant utilise les clés Stripe de la plateforme. Vous pouvez configurer des clés spécifiques au tenant ci-dessous.",
            )}
          />
        )}

        {isTenantConnected && !isEditing && (
          <Space direction="vertical" size="middle" className="mb-4 w-full">
            {status?.connectedAt && (
              <Text type="secondary">
                {translate(
                  "invoice-settings.integrations.stripe.connectedAt",
                  "Connecté le",
                )}{" "}
                {new Date(status.connectedAt).toLocaleString()}
              </Text>
            )}
            {status?.secretKeyLast4 && (
              <Text>
                {translate(
                  "invoice-settings.integrations.stripe.secretKeyMasked",
                  "Clé secrète",
                )}
                {`: …${status.secretKeyLast4}`}
              </Text>
            )}
            {status?.webhookUrl && (
              <div>
                <Text strong className="mb-1 block">
                  {translate(
                    "invoice-settings.integrations.stripe.webhookUrlLabel",
                    "URL du webhook",
                  )}
                </Text>
                <Space wrap>
                  <Text code copyable={false} className="break-all">
                    {status.webhookUrl}
                  </Text>
                  <Button
                    icon={<CopyOutlined />}
                    onClick={handleCopyWebhookUrl}
                    size="small"
                  >
                    {translate(
                      "invoice-settings.integrations.stripe.copy",
                      "Copier",
                    )}
                  </Button>
                </Space>
                <Paragraph type="secondary" className="!mt-2 !mb-0">
                  {translate(
                    "invoice-settings.integrations.stripe.webhookHint",
                    "Dans le Dashboard Stripe, créez un endpoint pour l'événement checkout.session.completed pointant vers cette URL.",
                  )}
                </Paragraph>
              </div>
            )}
            <Space>
              <Button onClick={() => setIsEditing(true)}>
                {translate(
                  "invoice-settings.integrations.stripe.updateKeys",
                  "Mettre à jour les clés",
                )}
              </Button>
              <Button
                danger
                icon={<DisconnectOutlined />}
                loading={isDisconnecting}
                onClick={handleDisconnect}
              >
                {translate(
                  "invoice-settings.integrations.stripe.disconnect",
                  "Supprimer la config tenant",
                )}
              </Button>
            </Space>
          </Space>
        )}

        {showForm && (
          <Form
            form={form}
            layout="vertical"
            onFinish={handleSave}
            requiredMark={false}
          >
            {(isEnvFallback || status?.source === "none") &&
              status?.webhookUrl && (
                <Form.Item
                  label={translate(
                    "invoice-settings.integrations.stripe.webhookUrlLabel",
                    "URL du webhook",
                  )}
                >
                  <Space wrap>
                    <Text code className="break-all">
                      {status.webhookUrl}
                    </Text>
                    <Button
                      icon={<CopyOutlined />}
                      onClick={handleCopyWebhookUrl}
                      size="small"
                    >
                      {translate(
                        "invoice-settings.integrations.stripe.copy",
                        "Copier",
                      )}
                    </Button>
                  </Space>
                  <Paragraph type="secondary" className="!mt-2 !mb-0">
                    {translate(
                      "invoice-settings.integrations.stripe.webhookHint",
                      "Dans le Dashboard Stripe, créez un endpoint pour l'événement checkout.session.completed pointant vers cette URL.",
                    )}
                  </Paragraph>
                </Form.Item>
              )}
            <Form.Item
              name="secretKey"
              label={translate(
                "invoice-settings.integrations.stripe.secretKeyLabel",
                "Clé secrète (Secret key)",
              )}
              rules={[
                {
                  required: true,
                  message: translate(
                    "invoice-settings.integrations.stripe.secretKeyRequired",
                    "La clé secrète est requise",
                  ),
                },
              ]}
            >
              <Input.Password
                placeholder="sk_live_… / sk_test_…"
                autoComplete="off"
              />
            </Form.Item>
            <Form.Item
              name="webhookSecret"
              label={translate(
                "invoice-settings.integrations.stripe.webhookSecretLabel",
                "Secret de signature du webhook",
              )}
              rules={[
                {
                  required: true,
                  message: translate(
                    "invoice-settings.integrations.stripe.webhookSecretRequired",
                    "Le secret webhook est requis",
                  ),
                },
              ]}
            >
              <Input.Password placeholder="whsec_…" autoComplete="off" />
            </Form.Item>
            <Space>
              <Button
                type="primary"
                htmlType="submit"
                icon={<SaveOutlined />}
                loading={isSaving}
              >
                {translate(
                  "invoice-settings.integrations.stripe.save",
                  "Enregistrer",
                )}
              </Button>
              {isEditing && (
                <Button
                  onClick={() => {
                    setIsEditing(false);
                    form.resetFields();
                  }}
                >
                  {translate("invoice-settings.cancel", "Annuler")}
                </Button>
              )}
            </Space>
          </Form>
        )}
      </Modal>
    </>
  );
};
